import { Dispatch, MouseEvent, MouseEventHandler, SetStateAction } from 'react'
import {
  Avatar,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import DeleteIcon from '@mui/icons-material/Delete'

import { AddEntityBtn, HelperWrapper, MoreTableButton, TableRelationIcon } from 'src/components'
import {
  useCancelImpactsRemove,
  useItemsRemoveConfirm,
  useMe,
  useNotification,
  useRemoveImpacts,
  useTableItemsCheck,
} from 'src/hooks'

import { useStyles } from './styles'
import { ETranslationNs, IImpactDtoWithId } from 'src/types'
import { TrashTimeoutIcon } from 'src/assets/icons'
import { getTableTimestamp, joinAbsRoute } from 'src/common/utils'
import { ROUTES } from 'src/constants'
import { IFilter } from 'src/components/filter/types/filter.types'

interface IImpactsTableProps {
  impacts?: IImpactDtoWithId[] | void
  sort: string
  filter?: IFilter
  setSort: Dispatch<SetStateAction<string>>
}

const ImpactsTable = ({ impacts, sort, filter, setSort }: IImpactsTableProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Navbar])
  const { setRemoveConfirm } = useItemsRemoveConfirm()
  const navigate = useNavigate()
  const { mutateAsync: cancelRemove } = useCancelImpactsRemove()
  const { mutateAsync } = useRemoveImpacts()
  const { notify } = useNotification()
  const { accountId } = useParams()
  const {
    handleAllItemsCheck,
    selectedItemsIds,
    handleItemCheck,
    isItemSelected,
    setSelectedItemsIds,
    isAllChecked,
    isIndeterminated,
    isAllUnchecked,
  } = useTableItemsCheck({
    items: impacts ?? [],
  })
  const { data: user, isLoading: isMeLoading } = useMe()
  const classes = useStyles()
  const [searchParams] = useSearchParams()
  const isFilterOpened = searchParams.get('open')

  const theme = useTheme()

  const handleItemsRemove = async () => {
    if (!selectedItemsIds.length) return

    const selectedItems = impacts!.filter((item) => selectedItemsIds.includes(item.id))
    const stepsRelations = selectedItems.flatMap(({ steps }) => steps.map(({ name }) => name))

    setRemoveConfirm({
      handleConfirm: async () => {
        const resp = await mutateAsync(selectedItemsIds)
        if (!resp) return

        notify({
          message: t('items-delete-notification.v'),
          icon: <TrashTimeoutIcon />,
          onButtonClick: async () => {
            await cancelRemove({ ids: selectedItemsIds })
          },
          timeout: 14000,
        })
        setSelectedItemsIds([])
      },
      itemsTitles: selectedItems.map((item) => item.name),
      itemsRelations: stepsRelations.length
        ? [
            {
              title: 'steps',
              relations: stepsRelations,
            },
          ]
        : [],
      itemsType: selectedItemsIds.length > 1 ? 'impacts' : 'impact',
    })
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleImpactClick = (id: string) => () => {
    impacts && navigate(`${id}`, { state: { filter, layoutFormat: 1, isFilterOpened } })
  }

  const handleStepClick =
    (id: string): MouseEventHandler<HTMLButtonElement> =>
    (e) => {
      e.stopPropagation()
      navigate(joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.STEPS, id))
    }

  const handleProductClick =
    (id: string): MouseEventHandler<HTMLButtonElement> =>
    (e) => {
      e.stopPropagation()
      navigate(joinAbsRoute(ROUTES.ACCOUNTS, accountId, ROUTES.PRODUCTS, id))
    }

  const handleInRowClick = (e: any) => {
    e.stopPropagation()
  }

  const handleDuplicateClick = (id: string) => (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    navigate(`add?duplicate=${id}`)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ border: 0 }} sx={{ 'td, th': { border: 0 } }}>
              <TableCell align="center">
                <Checkbox
                  indeterminate={isIndeterminated}
                  sx={{ color: theme.palette.custom.grey2 }}
                  onChange={handleAllItemsCheck}
                  checked={isAllChecked}
                />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('code')}
                  active={sort.split('-')[0] === 'code'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t={'table-code-column'} />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  onClick={handleSortChange('name')}
                  active={sort.split('-')[0] === 'name'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                >
                  <HelperWrapper t="impacts-table-account-name-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="table-product-column" ns={ETranslationNs.Common} />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="steps-header-title" ns={ETranslationNs.Navbar} />
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="un-sdg-title-abbr" />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sort.split('-')[0] === 'updated_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <IconButton disabled={isAllUnchecked} onClick={handleItemsRemove}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {impacts?.map((row) => (
              <TableRow
                onClick={handleImpactClick(row.id)}
                key={row.id}
                sx={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                  cursor: 'pointer',
                  'td, th': { border: 0 },
                  bgcolor: row.ai_generated
                    ? theme.palette.custom.lightGreen
                    : theme.palette.custom.white,
                }}
              >
                <TableCell align="center">
                  <Checkbox
                    checked={isItemSelected(row.id)}
                    onChange={handleItemCheck(row.id)}
                    sx={{ color: theme.palette.custom.grey2 }}
                    onClick={handleInRowClick}
                  />
                </TableCell>
                <TableCell>
                  <div className={classes.imageWrapperProduct}>
                    <Avatar alt={row.name} src={row.image_small || row.image} />
                  </div>
                </TableCell>
                <TableCell>
                  <p style={{ whiteSpace: 'pre-line' }}>
                    {<strong>{row.name}</strong>}
                    {'\n'}#{row.code}
                  </p>
                </TableCell>
                <TableCell>
                  <div
                    className={`${classes.selectionWrapper} ${classes.selectionWrapperCentered}`}
                  >
                    {row.products.map((product) => {
                      return (
                        <TableRelationIcon
                          key={product.id}
                          handleClick={handleProductClick(product.id)}
                          iconAlt={product.name}
                          icon={product.image_small || product.image}
                          title={product.name}
                          subtitle={`#${product.code}`}
                        />
                      )
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    className={`${classes.selectionWrapper} ${classes.selectionWrapperCentered}`}
                  >
                    {row.steps.map((step) => {
                      return (
                        <TableRelationIcon
                          key={step.id}
                          handleClick={handleStepClick(step.id)}
                          iconAlt={step.name}
                          icon={step.image_small || step.image}
                          title={step.name}
                          subtitle={`#${step.code}`}
                          locationNames={step?.locations}
                        />
                      )
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.selectionWrapper}>
                    {row.un_sdg.map((unSdg) => {
                      return (
                        <TableRelationIcon
                          key={unSdg.id}
                          iconAlt={unSdg.name}
                          icon={unSdg.image_small || unSdg.image}
                          title={unSdg.name}
                          isSquared
                        />
                      )
                    })}
                  </div>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontSize: '10px',
                  }}
                >
                  {getTableTimestamp({ date: row.created_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontSize: '10px',
                  }}
                >
                  {getTableTimestamp({ date: row.updated_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center">
                  <MoreTableButton
                    items={[
                      {
                        label: t('duplicate-button-text.v'),
                        onClick: handleDuplicateClick(row.id),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AddEntityBtn upgrateTextLT="impact-create-upgrade-text" entityCountKey="impactsCount" />
    </>
  )
}

export { ImpactsTable }
